/**
 * Zod schemas for PI-RADS scoring annotation extension.
 */
import { z } from "zod"

export const piradsConfigSchema = z.object({
  type: z.literal("pirads")
})

const piradsPayloadShape = {
  laterality: z.enum(["left", "right", "middle", ""]).optional(),
  level: z.enum(["apex", "mid-apex", "mid", "base-mid", "base", "all levels", ""]).optional(),
  zones: z.array(z.enum(["AS", "PZa", "PZpl", "PZpm", "TZa", "TZp", "CZ"])).optional(),
  t2_score: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal("")]).optional(),
  t2_reason: z.string().optional(),
  t2_up_down_grade: z.string().optional(),
  dwi_score: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal("")]).optional(),
  dwi_reason: z.string().optional(),
  dwi_up_down_grade: z.string().optional(),
  dce_score: z.enum(["neg", "pos", ""]).optional(),
  dce_reason: z.string().optional(),
  isup_grade: z.enum(["", "unknown", "no-cancer", "1", "2", "3", "4", "5"]).optional()
} as const

export const createPiradsSchema = z.object({
  project_module_id: z.string().min(1),
  series_id: z.string().nullable().optional(),
  linked_annotation_id: z.string().nullable().optional(),
  ...piradsPayloadShape
})

export const updatePiradsSchema = z.object({
  linked_annotation_id: z.string().nullable().optional(),
  ...piradsPayloadShape
})

// ---------------------------------------------------------------------------
// Completion-requirement configuration (per project_modules.config)
// ---------------------------------------------------------------------------

/**
 * Fields a project admin may require for module completion.
 * Kept in sync with `PIRADS_TEXT_FIELDS` + `PIRADS_INT_FIELDS` (snake_case EAV keys).
 */
export const PIRADS_REQUIRABLE_FIELDS = [
  "laterality",
  "level",
  "zones",
  "t2_score",
  "t2_reason",
  "t2_up_down_grade",
  "dwi_score",
  "dwi_reason",
  "dwi_up_down_grade",
  "dce_score",
  "dce_reason",
  "isup_grade"
] as const

export type PiradsRequirableField = (typeof PIRADS_REQUIRABLE_FIELDS)[number]

/**
 * Default required-field set for PI-RADS completion.
 *
 * Note: `dce_score` and `dce_reason` are intentionally NOT required by default.
 * Project admins can opt-in per project via the modules settings UI.
 */
export const PIRADS_DEFAULT_REQUIRED_FIELDS: readonly PiradsRequirableField[] = [
  "laterality",
  "level",
  "zones",
  "t2_score",
  "t2_reason",
  "dwi_score",
  "dwi_reason"
]

export const piradsProjectConfigSchema = z.strictObject({
  required_fields: z.array(z.enum(PIRADS_REQUIRABLE_FIELDS)).optional()
})

export type PiradsProjectConfig = z.infer<typeof piradsProjectConfigSchema>

/**
 * Resolve the effective required-field list for a PI-RADS project module.
 * Falls back to defaults if config is null, invalid JSON, or fails validation.
 */
export function resolvePiradsRequiredFields(configJson: string | null | undefined): readonly PiradsRequirableField[] {
  if (!configJson) return PIRADS_DEFAULT_REQUIRED_FIELDS
  try {
    const raw = JSON.parse(configJson)
    const parsed = piradsProjectConfigSchema.safeParse(raw)
    if (!parsed.success) return PIRADS_DEFAULT_REQUIRED_FIELDS
    return parsed.data.required_fields ?? PIRADS_DEFAULT_REQUIRED_FIELDS
  } catch {
    return PIRADS_DEFAULT_REQUIRED_FIELDS
  }
}

/** Human-readable labels for error messages. */
export const PIRADS_FIELD_LABELS: Record<PiradsRequirableField, string> = {
  laterality: "Laterality",
  level: "Level",
  zones: "Zones",
  t2_score: "T2 score",
  t2_reason: "T2 reason",
  t2_up_down_grade: "T2 up/down grade",
  dwi_score: "DWI score",
  dwi_reason: "DWI reason",
  dwi_up_down_grade: "DWI up/down grade",
  dce_score: "DCE score",
  dce_reason: "DCE reason",
  isup_grade: "ISUP grade"
}
